import { createApi } from '@reduxjs/toolkit/query/react'
import {createSlice} from "@reduxjs/toolkit";
import {useSelector} from "react-redux";
import type {RootState} from "../store";

import { baseQuery } from './index'
import {INITIAL_DATA_REQUEST} from "../constants";

type ArticleQueryParams = {
    pageSize?: number;
    page?: number;
    search?: string;
    category?: string;
};

type TagsQueryParams = {
    articleIds?: Array<string | number>;
};

type ArticleSlugQueryParams = {
    slug?: string;
};

type BlogState = {
    articles: {
        data: {
            results: any[];
            count: number;
        };
        params: ArticleQueryParams;
    };
    tags: any[];
};

const initialState: BlogState = {
    articles: {
        data: {
            results: [],
            count: 0
        },
        params: {
            pageSize: 5,
            page: 1,
        },
    },
    tags: [],
};

export const blogApi = createApi({
        baseQuery,
        reducerPath: 'blogApi',
        endpoints: (builder) => ({
            getArticles: builder.query<any, ArticleQueryParams>({
                query: ({ pageSize, page, search, category } = {}) => {
                    const params = new URLSearchParams();

                    if (pageSize) params.append('pagination[pageSize]', String(pageSize));
                    if (page) params.append('pagination[page]', String(page));
                    if (category) params.append('filters[categories][slug][$eq]', category);
                    if (search) params.append('filters[body][$containsi]', search);

                    params.append('populate', '*');

                    return {
                        url: `/articles?${params.toString()}`,
                    };
                },
            }),
            getTags: builder.query<any, TagsQueryParams>({
                query: ({ articleIds } = {}) => {

                    const params = new URLSearchParams();

                    if (articleIds && articleIds.length > 0) {
                        articleIds.forEach((id) =>
                            params.append('filters[articles][id][$in][]', String(id))
                        );

                        articleIds.forEach((id) =>
                            params.append('populate[articles][filters][id][$in][]', String(id))
                        );
                    }

                    params.append('populate[articles][fields][0]', 'id');
                    params.append('fields[0]', 'name');
                    params.append('fields[1]', 'slug');

                    return `/categories?${params.toString()}`;

                }
            }),
            getArticle: builder.query<any, ArticleSlugQueryParams>({
                query: ({ slug } = {}) => {
                    const params = new URLSearchParams();
                    params.append('filters[slug][$eq]', slug);
                    params.append('populate', '*');
                    return `/articles?${params.toString()}`;
                },
            }),
        }),
    })

export const blogSlice = createSlice({
    name: 'blog',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addMatcher(
            blogApi.endpoints.getArticles.matchFulfilled,
            (state, { payload, meta: { arg: { originalArgs } }  }) => {
                state.articles = {
                    ...state.articles,
                    data: {
                        results: payload.data,
                        count: payload.meta.pagination.total,
                    },
                    params: {
                        ...state.articles.params,
                        ...originalArgs,
                    },
                };
            }
        )
        builder.addMatcher(
            blogApi.endpoints.getTags.matchFulfilled,
            (state, { payload }) => {
                state.tags = payload.data;
            }
        );
    },
})


export const { useLazyGetArticlesQuery, useLazyGetTagsQuery, useLazyGetArticleQuery } = blogApi

export const useArticles = () => {
    return useSelector((state: RootState) => state.blog.articles.data);
}

export const useArticlesCount = () => {
    return useSelector((state: RootState) => state.blog.articles.data.count);
}

export const useArticlesParams = () => {
    return useSelector((state: RootState) => state.blog.articles.params);
}

export const useTags = () => {
    return useSelector((state: RootState) => state.blog.tags);
}

export const useUpdateBlog = () => {
    return blogApi.util.upsertQueryData('getArticles', INITIAL_DATA_REQUEST, {
        results: [],
        count: 0
    })
}
